import React, { useEffect, useState } from "react";
import { Field, FormikErrors } from "formik";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "../../../../store";
import Question, { AnswerTypes } from "../../../../types/questionVM";
import SearchParams, { defaultParams } from "../../../../types/searchParams";
import {
  getJobTypeCategories,
  searchCategories,
} from "../../../../slices/category";
import { searchJobSource } from "../../../../slices/jobSource";
import { searchCommunicationMethod } from "../../../../slices/communicationMethod";
import { postSite, resetSite, searchSite } from "../../../../slices/site";
import { Inputs } from "../../../formInputs";
import JobType from "../../../../types/jobType";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { CustomJobType } from "../../JobType/QuestionPreview";
import { Base } from "../../../base";
import moment from "moment";
import ColumnDef from "../../../../types/columnDef";
import theme from "../../../../theme";
import {
  addJobMilestone,
  searchJobByType,
  updateJob,
} from "../../../../slices/job";
import JobMilestone from "../../../../types/jobMilestone";
import KeyValue from "../../../../types/keyValue";
import AddEditSiteForm from "../../../forms/AddEditSiteForm";
import { searchStates } from "../../../../slices/options";
import { searchSiteType } from "../../../../slices/siteType";
import { searchSiteManager } from "../../../../slices/siteManager";
import Site from "../../../../types/site";
import { closestCenter, DndContext, DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableSitePreview from "../../JobType/SortableSitePreview";

interface Props {
  question: Question;
  index: number;
  jobType: JobType;
  values: any;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<any>>;
}

const DisplayJobQuestions: React.FC<Props> = (props) => {
  const { question, index, jobType, values: job, setFieldValue } = props;
  const dispatch = useDispatch();

  const { jobTypeCategories } = useSelector((store) => store.category);
  const { jobSourceSearchResults } = useSelector((store) => store.jobSource);
  const { communicationMethodSearchResults } = useSelector(
    (store) => store.communication
  );
  const { siteSearchResults } = useSelector((store) => store.site);
  const { highlights } = useSelector((store) => store.highlight);

  const [siteFilter, setSiteFilter] = useState<SearchParams>(defaultParams);
  const [jobFilter, setJobFilter] = useState<SearchParams>(defaultParams);
  const [newSite, setNewSite] = useState<boolean>(false);
  const [deleteMilestone, setDeleteMilestone] = useState<{
    id?: number;
    value?: string;
  }>();
  const [jobByTypeResults, setJobByTypeResults] = useState<KeyValue[]>([]);

  useEffect(() => {
    switch (question.answerType.id) {
      case AnswerTypes.Category:
        if (jobType.id > 0) {
          dispatch(getJobTypeCategories(jobType.id));
        }
        break;
      case AnswerTypes.Source:
        dispatch(searchJobSource(defaultParams));
        break;
      case AnswerTypes.Method:
        dispatch(searchCommunicationMethod(defaultParams));
        break;
      case AnswerTypes.Site:
        dispatch(searchSite(defaultParams));
        break;
      case AnswerTypes.Job:
        if (!!question.filterSelectedJobType) {
          let filters: string[] = [];
          if (!!question.filterSelectedJobStatuses.length) {
            question.filterSelectedJobStatuses.forEach((s) => {
              filters.push(`jobstatus:${s.id}`);
            });
          } else {
            filters.push(`jobtype:${question.filterSelectedJobType.id}`);
          }
          setJobFilter({ ...defaultParams, filters });
          dispatch(searchJobByType({ ...defaultParams, filters })).then(
            (result: any) => {
              setJobByTypeResults(result.payload.results);
            }
          );
        }
        break;
      default:
        break;
    }
  }, [
    dispatch,
    question.answerType.id,
    jobType.id,
    question.filterSelectedJobType,
    question.filterSelectedJobStatuses,
    highlights?.length,
  ]);

  const updateCategorySearch = debounce((query: string) => {
    dispatch(
      searchCategories({
        ...defaultParams,
        filters: [`jobtype:${jobType.id}`],
        query: query,
      })
    );
  }, 500);

  const updateJobSourceSearch = debounce((query: string) => {
    dispatch(
      searchJobSource({
        ...defaultParams,
        query: query,
      })
    );
  }, 500);

  const updateCommMethodSearch = debounce((query: string) => {
    dispatch(
      searchCommunicationMethod({
        ...defaultParams,
        query: query,
      })
    );
  }, 500);

  const updateSiteSearch = debounce((query: string) => {
    dispatch(searchSite({ ...siteFilter, query }));
    setSiteFilter({ ...siteFilter, query });
  }, 500);

  const updateJobSearch = debounce((query: string) => {
    dispatch(searchJobByType({ ...jobFilter, query })).then((result: any) => {
      setJobByTypeResults(result.payload.results);
    });
    setJobFilter({ ...jobFilter, query });
  }, 500);

  const updateSiteManagerSearch = debounce((search: SearchParams) => {
    dispatch(
      searchSiteManager({
        query: search.query,
        pageNumber: 1,
        pageSize: 10,
      })
    );
  }, 500);

  const updateSiteTypeSearch = debounce((search: SearchParams) => {
    dispatch(
      searchSiteType({
        query: search.query,
        pageNumber: 1,
        pageSize: 10,
      })
    );
  }, 500);

  const updateStateSearch = debounce((search: SearchParams) => {
    dispatch(
      searchStates({
        query: search.query,
        pageNumber: 1,
        pageSize: 63,
      })
    );
  }, 500);

  const handleResetSite = () => {
    dispatch(resetSite());
  };

  const handleCancel = () => {
    setNewSite(false);
    const filter = job.answers[index].value.filter(
      (s: { id: number; name: string }) => s.id !== -1
    );
    setFieldValue(`answers.${index}.value`, filter);
  };

  const handleSubmitSite = (values: any) => {
    dispatch(
      postSite({
        ...values,
        address: {
          ...values.address,
          zipCode:
            values.address.zipCode === undefined
              ? undefined
              : `${values.address.zipCode}`,
        },
      })
    ).then((result: any) => {
      setNewSite(false);
      const newSite: Site = result.payload;
      setFieldValue(`answers.${index}.value`, [
        ...job.answers[index].value,
        {
          id: newSite.id,
          value: `${newSite.name} ${`(${newSite?.siteType?.value})`}${
            newSite.siteNumber ? `: ${newSite?.siteNumber}` : ""
          }`,
          address:
            newSite.address?.line1 &&
            newSite.address.city &&
            newSite.address.zipCode
              ? `${newSite.address.line1} ${newSite.address.line2 ?? ""} ${
                  newSite.address.zipCode
                }, ${newSite.address.city}`
              : "",
          order: job.answers[index].value.length + 1,
        },
      ]);
    });
  };

  const handleAddMilestone = (
    values: { start: Date; end?: Date; milestoneTitle?: string },
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean
    ) => Promise<void | FormikErrors<CustomJobType>>
  ) => {
    if (job.id > 0) {
      dispatch(addJobMilestone({ ...values, jobId: job.id }));
    } else {
      dispatch(
        updateJob({
          ...job,
          jobMilestones: [...job.jobMilestones, { ...values, jobId: 0 }],
        })
      );
    }
    setFieldValue("milestone", undefined);
    setFieldValue("milestoneTitle", "");
  };

  const columns: ColumnDef[] = [
    {
      field: "start",
      headerName: "Start",
      align: "center",
      headerAlign: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "end",
      headerName: "End",
      align: "center",
      headerAlign: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "name",
      headerName: "Title",
      align: "center",
      headerAlign: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      filterable: false,
      renderCell: (params) => <>{params.value}</>,
      mobileSize: 12,
    },
  ];

  const displayRows = (array: any[]) => {
    return array.map((milestone: JobMilestone) => {
      let rowObject = {
        id: milestone.id,
        name: milestone.title,
        start: `${moment(milestone.start).format("MM/DD/YYYY")}`,
        end: `${moment(milestone.end).format("MM/DD/YYYY")}`,
        actions: (
          <Base.TableActions
            id={milestone.id!}
            handleDelete={() => {
              setDeleteMilestone({ id: milestone.id, value: milestone.title });
            }}
          />
        ),
      };
      return rowObject;
    });
  };

  const renderOption = (
    props: React.HtmlHTMLAttributes<HTMLLIElement>,
    option: any
  ) => {
    return (
      <li
        {...props}
        key={option.value}
        style={{ backgroundColor: theme.palette.background.paper }}
      >
        <Typography
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              height: "25px",
              width: "25px",
              backgroundColor: option.color,
            }}
          />
          {option.name}
        </Typography>
      </li>
    );
  };

  const dateHighlightContainer = (children: React.ReactElement) => {
    return (
      <Grid container alignItems={"center"} columnSpacing={2}>
        <Grid
          size={
            !!job.answers[index].value &&
            moment(job.answers[index].value).isValid() &&
            !job.answers[index].dateHighlight
              ? 10
              : !!job.answers[index].dateHighlight
              ? 6
              : 12
          }
        >
          {children}
        </Grid>
        {!!job.answers[index].value &&
          moment(job.answers[index].value).isValid() &&
          !job.answers[index].calendarHighlight && (
            <Grid size={2}>
              <Field
                name={`answers.${index}.dateHighlight`}
                label="Highlight on Calendar"
                component={Inputs.Checkbox}
              />
            </Grid>
          )}
        {!!job.answers[index].dateHighlight && (
          <Grid
            size={!!job.answers[index].calendarHighlight ? 6 : 4}
            mb={"-23px"}
          >
            <Field
              name={`answers.${index}.calendarHighlight`}
              label="Highlight Type"
              component={Inputs.Dropdown}
              options={highlights}
              staticFilter
              renderOption={renderOption}
            />
          </Grid>
        )}
      </Grid>
    );
  };

  interface SiteAnswer {
    id?: number;
    value?: string;
    order?: number;
  }

  const reorder = (e: DragEndEvent) => {
    const { over, active } = e;

    if (!over) return;

    const oldIndex = job.answers[index].value.indexOf(
      job.answers[index].value.find((s: SiteAnswer) => s.order === active.id)!
    );
    const newIndex = job.answers[index].value.indexOf(
      job.answers[index].value.find((s: SiteAnswer) => s.order === over.id)!
    );

    const move = (from: number, to: number) => {
      const updated = [...job.answers[index].value];
      const [movedItem] = updated.splice(from, 1);
      updated.splice(to, 0, movedItem);
      setFieldValue(`answers.${index}.value`, updated);
    };
    move(oldIndex, newIndex);
  };

  switch (question.answerType.id) {
    case AnswerTypes.Text:
    case AnswerTypes.Phone:
    case AnswerTypes.Title:
      return (
        <Field
          name={`answers.${index}.value`}
          component={Inputs.Text}
          inputProps={{ maxLength: 100 }}
          label={question.questionText}
          required={question.required}
        />
      );
    case AnswerTypes.Status:
      return (
        <Field
          name={`answers.${index}.value`}
          component={Inputs.Dropdown}
          options={jobType.statuses.map((s) => {
            return { id: s.id, value: s.name };
          })}
          staticFilter
          label={question.questionText}
          required={question.required}
        />
      );
    case AnswerTypes.Checkbox:
      return (
        <Field
          name={`answers.${index}.value`}
          component={Inputs.Checkbox}
          label={question.questionText}
          required={question.required}
        />
      );
    case AnswerTypes.Multiline:
      return (
        <Field
          name={`answers.${index}.value`}
          component={Inputs.TextArea}
          label={question.questionText}
          required={question.required}
        />
      );
    case AnswerTypes.Dates:
      // TODO: Fix bug where Adding date breaks front end
      return (
        <Grid container columnSpacing={2} mt={2}>
          <Grid size={{ sm: 3, xs: 12 }}>
            <Field name="start" label="Start" component={Inputs.Date} />
          </Grid>
          <Grid size={{ sm: 3, xs: 12 }}>
            <Field name="end" label="End" component={Inputs.Date} />
          </Grid>
          <Grid size={{ sm: 6, xs: 12 }}>
            <Field
              name="milestoneTitle"
              label="Title"
              component={Inputs.Text}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Grid container direction={"row"} justifyContent={"flex-end"}>
              <Grid>
                <Base.Button
                  text="Add Date"
                  onClick={() => {
                    handleAddMilestone(
                      {
                        start: job.start,
                        end: job.end ?? job.start,
                        milestoneTitle: job.milestoneTitle,
                      },
                      setFieldValue
                    );
                  }}
                  disabled={
                    !job.start ||
                    !job.milestoneTitle ||
                    new Date(job.start!) > new Date(job.end!)
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Grid
              size={{ xs: 12 }}
              sx={{
                "& .header": {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                },
              }}
            >
              <Base.TableContainer
                columns={columns}
                rows={displayRows(job.jobMilestones ?? [])}
                mobileRows={displayRows(job.jobMilestones ?? [])}
                totalRows={job.jobMilestones.length}
                pageSize={5}
                highlightId={deleteMilestone?.id}
              />
            </Grid>
          </Grid>
          <Base.Dialog
            open={deleteMilestone?.id !== undefined}
            title={`Are you sure you would like to delete ${deleteMilestone?.value}?`}
            handleCancel={() =>
              setDeleteMilestone({ id: undefined, value: "" })
            }
            confirmText="Delete"
            confirmButtonType="delete"
            handleConfirm={() => {
              const filteredMilestones = job.jobMilestones
                .slice()
                .filter((m: any) => m.id !== deleteMilestone?.id);
              dispatch(
                updateJob({ ...job, jobMilestones: filteredMilestones })
              );
              setDeleteMilestone({ id: undefined, value: undefined });
            }}
            loading={false}
          />
        </Grid>
      );
    case AnswerTypes.DateTime:
      return (
        <>
          {dateHighlightContainer(
            <Field
              name={`answers.${index}.value`}
              component={Inputs.DateTime}
              label={question.questionText}
              required={question.required}
            />
          )}
        </>
      );
    case AnswerTypes.MultipleChoice:
      return (
        <Field
          name={`answers.${index}.value`}
          component={Inputs.Dropdown}
          options={question.choices}
          staticFilter
          label={question.questionText}
          required={question.required}
          multiple={question.multipleChoice}
          freeSolo={question.freeSolo}
        />
      );
    case AnswerTypes.Site:
      return (
        <>
          <Grid container spacing={2}>
            <Grid size={{ sm: 6, xs: 12 }}>
              <Field
                name={`answers.${index}.site`}
                component={Inputs.Dropdown}
                options={
                  siteSearchResults?.results && siteFilter.query
                    ? siteSearchResults?.results.map((site: Site) => {
                        return {
                          id: site.id,
                          value: `${site.name} ${
                            site.id > 0 ? `(${site?.siteType?.value})` : ""
                          }${
                            site.id > 0 && site.siteNumber
                              ? `: ${site?.siteNumber}`
                              : ""
                          }`,
                          address:
                            site.address?.line1 &&
                            site.address.city &&
                            site.address.zipCode
                              ? `${site.address.line1} ${
                                  site.address.line2 ?? ""
                                } ${site.address.zipCode}, ${site.address.city}`
                              : "",
                        };
                      })
                    : []
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  updateSiteSearch(e.currentTarget.value);
                }}
                onSelected={(e: React.SyntheticEvent, value: any) => {
                  if (!!value && value.id === -1) {
                    setNewSite(true);
                    setFieldValue(`answers.${index}.site`, undefined);
                  } else if (!!value && value.id > 0) {
                    setFieldValue(`answers.${index}.value`, [
                      ...job.answers[index].value,
                      { ...value, order: job.answers[index].value.length + 1 },
                    ]);
                    setFieldValue(`answers.${index}.site`, undefined);
                  }
                }}
                noOptionsText={
                  siteFilter.query === "" ? "Start typing to search" : undefined
                }
                label={question.questionText}
                required={question.required}
                clearAfterSelect
              />
            </Grid>
            <Grid
              size={{ sm: 6, xs: 12 }}
              sx={{
                maxHeight: "215px",
                overflow: "scroll",
                border: `1px solid ${theme.palette.text.primary}50`,
                borderRadius: "5px",
              }}
            >
              <DndContext
                onDragEnd={reorder}
                collisionDetection={closestCenter}
              >
                <ul style={{ width: "100%", margin: 0, padding: 0 }}>
                  <Typography align="center">Selected Sites</Typography>
                  <Grid container direction={"column"} spacing={2}>
                    <SortableContext
                      items={
                        job.answers[index].value
                          ? job.answers[index].value.map(
                              (s: SiteAnswer) => s.order!
                            )
                          : []
                      }
                      strategy={verticalListSortingStrategy}
                    >
                      {!!job.answers[index].value &&
                        job.answers[index].value.map((s: SiteAnswer) => (
                          <SortableSitePreview
                            removeQuestion={() => {
                              const filtered = job.answers[index].value.filter(
                                (s2: SiteAnswer) => s2.id !== s.id
                              );
                              setFieldValue(
                                `answers.${index}.value`,
                                filtered.map((s: SiteAnswer, i: number) => {
                                  return { ...s, order: i + 1 };
                                })
                              );
                            }}
                            site={s!}
                          />
                        ))}
                    </SortableContext>
                  </Grid>
                </ul>
              </DndContext>
            </Grid>
          </Grid>
          <Base.Modal open={newSite} onClose={() => setNewSite(false)}>
            <Box
              sx={{
                width: 750,
                borderRadius: "10px",
                padding: "15px",
              }}
            >
              <AddEditSiteForm
                submitSite={handleSubmitSite}
                handleCancel={handleCancel}
                apiSiteManagerSearch={updateSiteManagerSearch}
                apiSiteTypeSearch={updateSiteTypeSearch}
                apiStateSearch={updateStateSearch}
                resetSite={handleResetSite}
              />
            </Box>
          </Base.Modal>
        </>
      );
    case AnswerTypes.Category:
      return (
        <Field
          name={`answers.${index}.value`}
          component={Inputs.Dropdown}
          options={jobTypeCategories}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            updateCategorySearch(e.currentTarget.value);
          }}
          label={question.questionText}
          required={question.required}
        />
      );
    case AnswerTypes.Source:
      return (
        <Field
          name={`answers.${index}.value`}
          component={Inputs.Dropdown}
          options={jobSourceSearchResults?.results || []}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            updateJobSourceSearch(e.currentTarget.value);
          }}
          label={question.questionText}
          required={question.required}
        />
      );
    case AnswerTypes.Method:
      return (
        <Field
          name={`answers.${index}.value`}
          component={Inputs.Dropdown}
          options={communicationMethodSearchResults?.results || []}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            updateCommMethodSearch(e.currentTarget.value);
          }}
          label={question.questionText}
          required={question.required}
        />
      );
    case AnswerTypes.WeeklyTask:
      return (
        <Grid container direction={"column"}>
          <Field
            name="highlight"
            component={Inputs.Checkbox}
            label="Weekly Task"
          />
          {!!job.highlight && (
            <Grid size={{ xs: 12 }}>
              <Field
                name={`answers.${index}.value`}
                component={Inputs.Date}
                disablePast
              />
            </Grid>
          )}
        </Grid>
      );
    case AnswerTypes.DateRange:
      return (
        <>
          <Grid container alignItems={"center"} columnSpacing={2}>
            <Grid
              size={
                Array.isArray(job.answers[index].value) &&
                !job.answers[index].value.every((x: any) => x === null) &&
                !job.answers[index].dateHighlight
                  ? 10
                  : !!job.answers[index].dateHighlight
                  ? 6
                  : 12
              }
            >
              <Field
                name={`answers.${index}.value`}
                label={question.questionText}
                component={Inputs.DateRange}
                required={question.required}
              />
            </Grid>
            {Array.isArray(job.answers[index].value) &&
              !job.answers[index].value.every((x: any) => x === null) &&
              !job.answers[index].calendarHighlight && (
                <Grid size={2} mb={"24px"}>
                  <Field
                    name={`answers.${index}.dateHighlight`}
                    label="Highlight on Calendar"
                    component={Inputs.Checkbox}
                  />
                </Grid>
              )}
            {!!job.answers[index].dateHighlight && (
              <Grid
                size={!!job.answers[index].calendarHighlight ? 6 : 4}
                mb={"-6px"}
              >
                <Field
                  name={`answers.${index}.calendarHighlight`}
                  label="Highlight Type"
                  component={Inputs.Dropdown}
                  options={highlights}
                  staticFilter
                  renderOption={renderOption}
                />
              </Grid>
            )}
          </Grid>
        </>
      );
    case AnswerTypes.Date:
      return (
        <>
          {dateHighlightContainer(
            <Field
              name={`answers.${index}.value`}
              label={question.questionText}
              component={Inputs.Date}
              required={question.required}
            />
          )}
        </>
      );
    case AnswerTypes.Time:
      return (
        <Field
          name={`answers.${index}.value`}
          label={question.questionText}
          component={Inputs.Time}
          required={question.required}
        />
      );
    case AnswerTypes.Section:
      return (
        <>
          {!!question.questionText.length && (
            <Typography variant="h4">{question.questionText}</Typography>
          )}
          <Divider />
        </>
      );
    case AnswerTypes.Job:
      return (
        <Field
          name={`answers.${index}.value`}
          label={question.questionText}
          component={Inputs.Dropdown}
          options={jobByTypeResults.concat(question.choices || [])}
          required={question.required}
          multiple={question.multipleChoice}
          freeSolo={question.freeSolo}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            updateJobSearch(e.currentTarget.value);
          }}
          onFocus={() => {
            updateJobSearch("");
          }}
        />
      );
    case AnswerTypes.Confidential:
      return (
        <Field
          name={`answers.${index}.value`}
          component={Inputs.Checkbox}
          label={"Confidential"}
          required={question.required}
          confidential
          fontVariant="h5"
        />
      );
    default:
      return <>Other</>;
  }
};

export default DisplayJobQuestions;
